// Small time helpers for the v10 interval model. Times are "HH:MM" (24h) strings,
// as produced by the scraper. We only touch a real clock at the "what time is now"
// edge — no date library, no moment prototype patches.

export function hhmmToMinutes(s) {
    if (s == null) return null;
    const parts = String(s).split(':');
    return (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
}

// "08:45" -> "8:45 AM"  (matches the old moment `.format('LT')` output)
export function formatTime(s) {
    if (s == null || s === '') return '';
    const parts = String(s).split(':');
    let h = parseInt(parts[0], 10);
    const m = parts[1];
    const ampm = h < 12 ? 'AM' : 'PM';
    h = h % 12;
    if (h === 0) h = 12;
    return `${h}:${m} ${ampm}`;
}

// Current cycle day: 1 = Monday .. 5 = Friday, or null on the weekend.
export function currentCycleDay(now) {
    now = now || new Date();
    const wd = now.getDay(); // 0 Sun .. 6 Sat
    return (wd >= 1 && wd <= 5) ? wd : null;
}

export function minutesNow(now) {
    now = now || new Date();
    return (now.getHours() * 60) + now.getMinutes();
}

// Merge overlapping/adjacent {start,end} minute intervals into a sorted, disjoint
// set. Used to turn several people's lessons into their combined busy time.
export function mergeIntervals(intervals) {
    const sorted = intervals.slice().sort((a, b) => a.start - b.start);
    const out = [];
    sorted.forEach(iv => {
        const last = out[out.length - 1];
        if (last && iv.start <= last.end) {
            last.end = Math.max(last.end, iv.end);
        } else {
            out.push({ start: iv.start, end: iv.end });
        }
    });
    return out;
}

// The complement of `busy` within [rangeStart, rangeEnd] — i.e. the free gaps.
// This is the meeting-maker's answer: pass everyone's combined busy time and get
// back the intervals when they are all free, by real clock time (not bell slot).
export function freeIntervals(busy, rangeStart, rangeEnd) {
    const merged = mergeIntervals(busy);
    const out = [];
    let cursor = rangeStart;
    for (const iv of merged) {
        if (iv.start >= rangeEnd) break; // nothing this interval can add inside the window
        if (iv.start > cursor) out.push({ start: cursor, end: Math.min(iv.start, rangeEnd) });
        cursor = Math.max(cursor, iv.end);
    }
    if (cursor < rangeEnd) out.push({ start: cursor, end: rangeEnd });
    return out;
}

// A short label ('T', '1'..'7', 'R', 'L', 'B') derived from a period name.
// Kept compatible with the old grid styling, which treats anything not in
// 'T1234567' as a short (narrow) row.
export function shortNameForPeriod(name) {
    if (!name) return 'X';
    const lessonMatch = name.match(/Lesson\s+(\d)/i);
    if (lessonMatch) return lessonMatch[1];
    if (/tutor/i.test(name)) return 'T';
    if (/recess/i.test(name)) return 'R';
    if (/lunch/i.test(name)) return 'L';
    if (/break|interval/i.test(name)) return 'B';
    return name[0];
}
